import React from 'react';
import { Icon, getCategory, getUser } from './components.jsx';
import { CATEGORIES } from './data.jsx';
// Knowledge base

// ── Auto-linkify URLs in plain-text article paragraphs ───────────────────────
// Scans a string for http(s) URLs and wraps each as an <a>. Trailing common
// punctuation (.,!?;:)) is preserved as plain text so we don't pull " or .
// into the link. Used by renderBlock for legacy JSON-block-array articles.
const URL_RE = /\bhttps?:\/\/[^\s<>"']+[^\s<>"',.!?;:)]/g;
const linkify = (text) => {
  if (typeof text !== 'string' || !text) return text;
  if (!URL_RE.test(text)) return text;
  URL_RE.lastIndex = 0;
  const out = [];
  let last = 0, m, i = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <a key={`l${i++}`} href={m[0]} target="_blank" rel="noopener noreferrer"
         style={{color:'var(--accent)', wordBreak:'break-all'}}>{m[0]}</a>
    );
    last = URL_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

// ── HTML body rendering with DOMPurify sanitisation ──────────────────────────
// New articles store body as an HTML string (Quill output). This renders it
// safely. Allowlist is tight: only formatting + links + images allowed.
// All <a> get target=_blank rel="noopener noreferrer" defensively.
// Block-level tags that indicate the body already has real structure.
const BLOCK_TAG_RE = /<(p|br|h[1-6]|ul|ol|li|table|tr|blockquote|div)[\s>/]/i;

const renderHtmlBody = (html) => {
  if (!html || typeof html !== 'string') return null;
  let src = html;
  // Some articles (a batch imported/pasted from external docs) were stored
  // as plain text with NO block structure - they render as one unreadable
  // run-on wall. If a body has no block tags but DOES retain line breaks,
  // promote blank lines to paragraphs and single breaks to <br> so it
  // reflows properly. Inline tags (e.g. <a> links) are preserved because
  // we only inject <p>/<br> and still run the whole thing through DOMPurify
  // below - so this adds NO new XSS surface. Bodies with no line breaks at
  // all can't be recovered here (the paragraph boundaries were lost at
  // import); the KB format audit (kb-format-audit.sql) lists those.
  if (!BLOCK_TAG_RE.test(src) && /\n/.test(src)) {
    const paras = src.replace(/\r\n?/g, '\n').split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
    if (paras.length) src = paras.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
  }
  const purifier = window.DOMPurify;
  if (!purifier) {
    // Safety net if the CDN failed - render as escaped text rather than
    // unsanitised HTML. Use the original html (pre-reflow) so the raw
    // source is visible; whiteSpace:pre-wrap keeps any line breaks.
    return <pre style={{whiteSpace:'pre-wrap', fontFamily:'inherit'}}>{html}</pre>;
  }
  const clean = purifier.sanitize(src, {
    ALLOWED_TAGS: ['p','br','b','i','u','strong','em','a','img','ul','ol','li',
                   'h1','h2','h3','span','code','pre','blockquote','s','strike'],
    ALLOWED_ATTR: ['href','src','alt','title','target','rel','style','class','width','height'],
    ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|\/|#)/i,
  });
  // Force target=_blank + rel on every link, regardless of what the author wrote.
  const wrap = document.createElement('div');
  wrap.innerHTML = clean;
  wrap.querySelectorAll('a').forEach(a => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  // Keep images from blowing out the layout.
  wrap.querySelectorAll('img').forEach(img => {
    const existing = img.getAttribute('style') || '';
    if (!/max-width/i.test(existing)) {
      img.setAttribute('style', `${existing}; max-width: 100%; height: auto;`);
    }
  });
  return <div className="kb-html-body" dangerouslySetInnerHTML={{__html: wrap.innerHTML}}/>;
};

const fmtKbDate = (updatedMins) => {
  const d = new Date(Date.now() + updatedMins * 60 * 1000);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Format an ISO timestamp (e.g. article.createdAt) as a date.
const fmtKbIsoDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Resolve a stored author string into a display name. Tries, in order:
//  1. Existing USERS slug (rob -> Rob Smith, if present)
//  2. Exact email match in window.STAFF_LIST (from Graph at sign-in)
//  3. Username with dots/underscores stripped vs. STAFF_LIST mail-local-part
//     (so 'jamesclydesdale' matches 'james.clydesdale@leeandthompson.com')
//  4. Fallback: whatever was stored (so we never show blank).
const resolveAuthorName = (author) => {
  if (!author) return 'Unknown';
  const u = getUser(author);
  if (u) return u.name;
  const authorLc = String(author).toLowerCase();
  const staff = window.STAFF_LIST || [];
  if (authorLc.includes('@')) {
    const match = staff.find(s => (s.mail || s.userPrincipalName || '').toLowerCase() === authorLc);
    if (match) return match.displayName;
    return author.split('@')[0];
  }
  const match = staff.find(s => {
    const mail = (s.mail || s.userPrincipalName || '').toLowerCase();
    const local = mail.split('@')[0].replace(/[._-]/g, '');
    return local === authorLc;
  });
  return match ? match.displayName : author;
};

const KBView = ({ state, dispatch, currentUser }) => {
  // selectedCat values: null = All, '__favourites' = Favourites, '__mine' = My articles,
  // '__flows:<subSection>' = a Power Automate flow sub-section, otherwise a regular category id.
  const [selectedCat, setSelectedCat] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const [openId, setOpenId] = React.useState(state.kbOpenId || null);
  // Sort: 'newest' (default), 'oldest', 'az', 'za'
  const [sortBy, setSortBy] = React.useState('newest');

  // Power Automate flow guides - loaded once from /api/flows (IT-gated,
  // bearer-token-required). Files live in functions/flows/ on the Function
  // App, NOT in the SWA static deploy - so anyone fetching them on the
  // public internet without a valid IT-group token gets 403. See security.md
  // §39 + §77: every data endpoint must require IT-group membership.
  const [flows, setFlows] = React.useState([]);
  const [flowsError, setFlowsError] = React.useState(null);
  const [openFlowId, setOpenFlowId] = React.useState(null);
  // HTML body for the currently-open flow guide. Fetched on demand, cached
  // by id so re-opening the same flow doesn't re-fetch.
  const [flowHtmlCache, setFlowHtmlCache] = React.useState({});
  const [flowHtmlLoading, setFlowHtmlLoading] = React.useState(false);

  React.useEffect(() => {
    window.IT_AUTH.api('GET', '/flows', undefined, 'kbFlows')
      .then(data => setFlows(Array.isArray(data?.flows) ? data.flows : []))
      .catch(err => {
        console.warn('[kb] flow manifest fetch failed:', err.message);
        setFlowsError(err);
        setFlows([]);
      });
  }, []);

  // Fetch the open flow's HTML body on demand, cache per-id.
  React.useEffect(() => {
    if (!openFlowId) return;
    if (flowHtmlCache[openFlowId]) return;
    setFlowHtmlLoading(true);
    window.IT_AUTH.api('GET', `/flows/${openFlowId}`, undefined, 'kbFlowHtml')
      .then(data => {
        setFlowHtmlCache(prev => ({ ...prev, [openFlowId]: data?.html || '' }));
      })
      .catch(err => {
        console.warn('[kb] flow html fetch failed:', err.message);
        setFlowHtmlCache(prev => ({ ...prev, [openFlowId]: `<p style="padding:32px;font-family:sans-serif;color:#dc2626">Could not load guide: ${err.message}</p>` }));
      })
      .finally(() => setFlowHtmlLoading(false));
  }, [openFlowId, flowHtmlCache]);

  // Render the open guide from a blob: URL rather than srcdoc. srcdoc iframes
  // can't do in-page anchor navigation - clicking a TOC link tries to navigate
  // to about:srcdoc#id, which browsers can't re-render, so the links break and
  // blank the view. A blob: URL gives the iframe a real document URL, so the
  // guide's own table-of-contents works. sandbox="" still isolates it (opaque
  // origin, no scripts, no parent access). Revoked when the guide changes/closes.
  const openFlowHtml = openFlowId ? flowHtmlCache[openFlowId] : null;
  const [flowBlobUrl, setFlowBlobUrl] = React.useState(null);
  React.useEffect(() => {
    if (openFlowHtml == null) { setFlowBlobUrl(null); return undefined; }
    const url = URL.createObjectURL(new Blob([openFlowHtml], { type: 'text/html' }));
    setFlowBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [openFlowHtml]);

  // Group flows by top-level section, then sub-section, for sidebar rendering.
  // `section` defaults to 'Power Automate' (the original grouping). A guide can
  // opt into another section (e.g. 'Claude Firm Apps') via the manifest.
  const flowsBySection = React.useMemo(() => {
    const secs = new Map(); // section -> Map(subSection -> items[])
    for (const f of flows) {
      const sec = f.section || 'Power Automate';
      const sub = f.subSection || 'Other';
      if (!secs.has(sec)) secs.set(sec, new Map());
      const subs = secs.get(sec);
      if (!subs.has(sub)) subs.set(sub, []);
      subs.get(sub).push(f);
    }
    // Power Automate first, any other section after (alphabetical); sub-sections
    // alphabetical within each.
    const rank = (s) => (s === 'Power Automate' ? 0 : 1);
    return Array.from(secs.entries())
      .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
      .map(([sec, subs]) => [sec, Array.from(subs.entries()).sort((x, y) => x[0].localeCompare(y[0]))]);
  }, [flows]);

  React.useEffect(() => {
    if (state.kbOpenId) setOpenId(state.kbOpenId);
  }, [state.kbOpenId]);

  // KB articles come from the API via state.kbArticles. The || [] guards
  // against state having no array (e.g. before the initial fetch completes).
  const articles = state.kbArticles || [];

  // Current user's identity - handle both full email and just-local-part formats.
  // Imported articles have author='jamesclydesdale'; new articles have author='jamesclydesdale@leeandthompson.com'.
  const myEmail = (currentUser?.mail || currentUser?.userPrincipalName || '').toLowerCase();
  const myLocal = myEmail.split('@')[0];
  const isMine = (author) => {
    const a = (author || '').toLowerCase();
    if (!a) return false;
    const aLocal = a.split('@')[0];
    return a === myEmail || aLocal === myLocal;
  };

  // Filter
  const filtered = articles.filter(a => {
    if (selectedCat === '__favourites') {
      if (!a.isFavourited) return false;
    } else if (selectedCat === '__mine') {
      if (!isMine(a.author)) return false;
    } else if (selectedCat) {
      if (a.category !== selectedCat) return false;
    }
    if (query) {
      const q = query.toLowerCase();
      if (!a.title.toLowerCase().includes(q) && !a.summary.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Sort the filtered list. Date sorts use createdAt where available.
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'az')   return a.title.localeCompare(b.title);
    if (sortBy === 'za')   return b.title.localeCompare(a.title);
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return sortBy === 'oldest' ? ta - tb : tb - ta;
  });

  const counts = CATEGORIES.reduce((acc,c) => {
    acc[c.id] = articles.filter(a => a.category === c.id).length;
    return acc;
  }, {});
  const favouriteCount = articles.filter(a => a.isFavourited).length;
  const myCount = articles.filter(a => isMine(a.author)).length;

  const article = sorted.find(a => a.id === openId) || articles.find(a => a.id === openId);

  return (
    <div className={`kb${((openId && article) || openFlowId) ? ' kb-reading' : ''}`}>
      <div className="kb-side">
        <div style={{padding:"4px 10px 8px", fontSize:11, fontWeight:700, color:"var(--text)", letterSpacing:".02em"}}>Articles</div>
        {/* Every article-sidebar handler clears openFlowId - otherwise the
            flow iframe stays in front of the article list and the user has
            to use the Back button before any other nav works. */}
        <div className={`kb-cat ${!selectedCat ? "active" : ""}`} onClick={() => { setSelectedCat(null); setOpenId(null); setOpenFlowId(null); }}>
          {/* Was Icon name="layers" which doesn't exist in components.jsx, so
              the icon silently rendered nothing. Switched to "inbox" to match
              the Tickets sidebar's "All tickets" entry. */}
          <Icon name="inbox" size={12}/>
          <span>All articles</span>
          <span className="count">{articles.length}</span>
        </div>
        <div
          className={`kb-cat ${selectedCat === '__mine' ? "active" : ""}`}
          onClick={() => { setSelectedCat(selectedCat === '__mine' ? null : '__mine'); setOpenId(null); setOpenFlowId(null); }}
        >
          <Icon name="user" size={12}/>
          <span>My articles</span>
          <span className="count">{myCount}</span>
        </div>
        <div
          className={`kb-cat ${selectedCat === '__favourites' ? "active" : ""}`}
          onClick={() => { setSelectedCat(selectedCat === '__favourites' ? null : '__favourites'); setOpenId(null); setOpenFlowId(null); }}
          style={{ color: selectedCat === '__favourites' ? undefined : '#D97706' }}
        >
          <span style={{ marginRight: 4 }}>★</span>
          <span>My favourites</span>
          <span className="count">{favouriteCount}</span>
        </div>
        {CATEGORIES.map(c => (
          <div
            key={c.id}
            className={`kb-cat ${selectedCat === c.id ? "active" : ""}`}
            onClick={() => { setSelectedCat(c.id === selectedCat ? null : c.id); setOpenId(null); setOpenFlowId(null); }}
          >
            <span>{c.label}</span>
            <span className="count">{counts[c.id] || 0}</span>
          </div>
        ))}

        {/* Power Automate Flows - a second top-level grouping. These are
            static HTML guides under /flows/, not DB articles. "All flows"
            shows every guide regardless of sub-section; the sub-section
            entries (NetDocuments, Email, …) filter to that group. */}
        {flowsBySection.map(([section, subs]) => (
          <React.Fragment key={section}>
            <div style={{ padding: "16px 10px 8px", fontSize: 11, fontWeight: 700, color: "var(--text)", letterSpacing: ".02em" }}>
              {section === 'Power Automate' ? 'Power Automate Flows' : section}
            </div>
            {section === 'Power Automate' && (
              <div
                className={`kb-cat ${selectedCat === '__flowsall' ? "active" : ""}`}
                onClick={() => {
                  // If a flow viewer is open, clicking "All flows" should
                  // return to the list - not toggle the filter off, which
                  // would land on "All articles". Only collapse when actually
                  // in the list view.
                  if (openFlowId) {
                    setOpenFlowId(null);
                    setSelectedCat('__flowsall');
                    setOpenId(null);
                    return;
                  }
                  setSelectedCat(selectedCat === '__flowsall' ? null : '__flowsall');
                  setOpenId(null);
                }}
              >
                <Icon name="inbox" size={12}/>
                <span>All flows</span>
                <span className="count">{flows.filter(f => (f.section || 'Power Automate') === 'Power Automate').length}</span>
              </div>
            )}
            {subs.map(([subSection, items]) => {
              const key = `__flows:${subSection}`;
              const active = selectedCat === key;
              return (
                <div
                  key={key}
                  className={`kb-cat ${active ? "active" : ""}`}
                  onClick={() => {
                    // Single-guide sub-section: open the guide directly, skip
                    // the redundant one-item list.
                    if (items.length === 1) {
                      setSelectedCat(key);
                      setOpenFlowId(items[0].id);
                      setOpenId(null);
                      return;
                    }
                    // Multi-guide: if a flow viewer is open, navigate back to
                    // this sub-section list rather than toggling the filter off.
                    if (openFlowId) {
                      setOpenFlowId(null);
                      setSelectedCat(key);
                      setOpenId(null);
                      return;
                    }
                    setSelectedCat(active ? null : key);
                    setOpenId(null);
                  }}
                >
                  <span>{subSection}</span>
                  <span className="count">{items.length}</span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
        {flowsError && (
          <div style={{ padding: "8px 10px", fontSize: 11, color: "#dc2626" }}>
            Could not load flow manifest.
          </div>
        )}
      </div>

      <div className="kb-main">
        {/* Article toolbar - only shown when browsing articles. Power Automate
            flows are static HTML files (not authored in-app), so the New
            Article + Sort controls don't apply there. */}
        {!(selectedCat === '__flowsall' || selectedCat?.startsWith('__flows:') || openFlowId) && (
          <div className="kb-tools">
            <div className="kb-search">
              <Icon name="search" size={12}/>
              <input
                placeholder="Search articles…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              title="Sort articles"
              style={{
                padding: '6px 10px', fontSize: 12, borderRadius: 6,
                border: '1px solid var(--line)', background: 'var(--bg)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="az">Title A → Z</option>
              <option value="za">Title Z → A</option>
            </select>
            <button className="btn primary" onClick={() => dispatch({type:"openNewArticle"})}><Icon name="plus" size={11}/> New article</button>
          </div>
        )}

        {/* Power Automate flow viewer - when a flow is open, render its
            static HTML in an iframe for full style isolation. */}
        {openFlowId ? (
          (() => {
            const flow = flows.find(f => f.id === openFlowId);
            if (!flow) return null;
            const html = flowHtmlCache[openFlowId];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <button className="btn ghost" onClick={() => setOpenFlowId(null)} style={{ marginBottom: 12, alignSelf: 'flex-start' }}>
                  ← Back to {flow.subSection || 'flows'}
                </button>
                {html == null || flowHtmlLoading || !flowBlobUrl ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    Loading guide…
                  </div>
                ) : (
                  <iframe
                    src={flowBlobUrl}
                    title={flow.title}
                    // Loaded from a blob: URL (not srcdoc) so the guide's own
                    // in-page TOC anchors work (srcdoc can't navigate fragments).
                    // sandbox has allow-same-origin ONLY (so the page-origin blob
                    // loads) and deliberately NO allow-scripts - so no JS in a
                    // guide ever runs, even if one accidentally included a
                    // <script>. That "scripts never execute" property is the real
                    // isolation; without it, allow-same-origin is inert. The trust
                    // boundary also lives at the API layer (requireIT gates fetch).
                    sandbox="allow-same-origin"
                    style={{
                      flex: 1, width: '100%', minHeight: 600,
                      border: '1px solid var(--line)', borderRadius: 8,
                      background: '#fff',
                    }}
                  />
                )}
              </div>
            );
          })()
        ) : selectedCat === '__flowsall' || selectedCat?.startsWith('__flows:') ? (
          // Flow list - either "All flows" (every guide) or a single
          // sub-section. Both render the same card layout; the sub-section
          // label on each card means "All flows" is still easy to scan.
          (() => {
            // Sort by title - L&T-numbered flows naturally line up
            // (LT002 < LT009 < LT010 < LT012) and any future non-numbered
            // ones land in alphabetical order. Done after filtering so each
            // sub-section's view is also sorted.
            const sortFlows = (arr) => [...arr].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
            const items = sortFlows(
              selectedCat === '__flowsall'
                ? flows.filter(f => (f.section || 'Power Automate') === 'Power Automate')
                : flows.filter(f => (f.subSection || 'Other') === selectedCat.slice('__flows:'.length))
            );
            const matchesQuery = (f) => {
              if (!query) return true;
              const q = query.toLowerCase();
              return (f.title || '').toLowerCase().includes(q) || (f.summary || '').toLowerCase().includes(q);
            };
            const visible = items.filter(matchesQuery);
            return (
              <div className="kb-list">
                {visible.length === 0 && (
                  <div className="empty"><div><h3>No flows yet</h3><div>Send a flow ZIP to add the first guide here.</div></div></div>
                )}
                {visible.map(f => (
                  <div key={f.id} className="kb-item" onClick={() => setOpenFlowId(f.id)}>
                    <div style={{ minWidth: 0 }}>
                      <h3>{f.title}</h3>
                      <p>{f.summary}</p>
                      <div style={{ marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: "var(--text-subtle)" }}>{f.section || 'Power Automate'} · {f.subSection}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0 }}>
                      <div style={{ textAlign: "right", minWidth: 120 }}>
                        <div className="meta">{f.id}</div>
                        {f.addedAt && <div className="meta">Added {f.addedAt}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        ) : openId && article ? (
          <KBArticleView article={article} onBack={() => setOpenId(null)} dispatch={dispatch}/>
        ) : (
          <div className="kb-list">
            {sorted.length === 0 && (
              <div className="empty"><div><h3>No matches</h3><div>Try fewer filters or a broader search.</div></div></div>
            )}
            {sorted.map(a => (
              <div key={a.id} className="kb-item" onClick={() => setOpenId(a.id)}>
                <div style={{minWidth:0}}>
                  <h3>{a.title}</h3>
                  <p>{a.summary}</p>
                  <div style={{marginTop:4}}>
                    <span style={{fontSize:11, color:"var(--text-subtle)"}}>{getCategory(a.category)?.label}</span>
                  </div>
                </div>
                {/* Meta column has a min-width so the star stays at a consistent X across rows */}
                <div style={{display:"flex", alignItems:"flex-start", gap:12, flexShrink:0}}>
                  <div style={{textAlign:"right", minWidth: 120}}>
                    <div className="meta">KB-{a.id}</div>
                    <div className="meta">{resolveAuthorName(a.author)}</div>
                    <div className="meta">{fmtKbIsoDate(a.createdAt) || fmtKbDate(a.updated)}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dispatch({type:"toggleFavourite", id: a.id}); }}
                    title={a.isFavourited ? "Remove from favourites" : "Add to favourites"}
                    style={{
                      background: "none", border: "none", cursor: "pointer", padding: 4,
                      fontSize: 18, lineHeight: 1, width: 28, flexShrink: 0,
                      color: a.isFavourited ? "#D97706" : "var(--text-subtle)",
                      transition: "color 0.12s",
                      alignSelf: "flex-start",
                    }}
                  >{a.isFavourited ? "★" : "☆"}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const KBArticleView = ({ article, onBack, dispatch }) => {
  const authorName = resolveAuthorName(article.author);
  const createdDate = fmtKbIsoDate(article.createdAt) || fmtKbDate(article.updated);
  // Hydrate the article body on demand. The initial /kb list is loaded
  // with ?slim=1 to shave ~450KB off first paint, so bodies arrive here
  // as empty strings (or empty arrays for legacy block-format bodies).
  // Fetch the full row when the view opens, then dispatch a local-only
  // 'hydrateArticle' to merge it into state. Refires when navigating
  // between articles because article.id is in the deps array.
  React.useEffect(() => {
    const alreadyHydrated = Array.isArray(article.body)
      ? article.body.length > 0
      : !!(article.body && article.body.length);
    if (alreadyHydrated) return;
    let cancelled = false;
    window.IT_AUTH.api('GET', `/kb/${article.id}`, undefined, 'kbHydrate')
      .then(full => {
        if (cancelled || !full) return;
        dispatch({ type: 'hydrateArticle', id: article.id, article: full });
      })
      .catch(err => console.warn('[kb hydrate] failed:', err.message));
    return () => { cancelled = true; };
  }, [article.id]);
  return (
    <div className="kb-article">
      <button className="btn ghost" onClick={onBack} style={{marginBottom: 16}}>
        ← Back to articles
      </button>
      <div style={{display:"flex", alignItems:"flex-start", gap: 12}}>
        <h1 style={{flex: 1, margin: 0}}>{article.title}</h1>
        <button
          onClick={() => dispatch({type:"toggleFavourite", id: article.id})}
          title={article.isFavourited ? "Remove from favourites" : "Add to favourites"}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: "4px 8px",
            fontSize: 22, lineHeight: 1, flexShrink: 0,
            color: article.isFavourited ? "#D97706" : "var(--text-subtle)",
          }}
        >{article.isFavourited ? "★" : "☆"}</button>
      </div>
      <div className="a-meta" style={{marginBottom: 24, marginTop: 6}}>
        KB-{article.id} · {getCategory(article.category)?.label} · by {authorName} · created {createdDate}
      </div>
      {Array.isArray(article.body)
        ? article.body.map((block, i) => renderBlock(block, i))
        : renderHtmlBody(article.body || '')}
      <KBAttachmentsBlock articleId={article.id} />
      <div style={{marginTop: 32, padding: "16px 0", borderTop: "1px solid var(--line)", display:"flex", justifyContent:"flex-end"}}>
        <button className="btn ghost" onClick={() => dispatch({type:'openEditArticle', id: article.id})}>
          <Icon name="edit" size={11}/> Edit
        </button>
      </div>
    </div>
  );
};

// ─── KB attachments (feedback request: "Attach PDF's to KB's") ────────────────
// Mirrors the ticket AttachmentsBlock pattern. Helpers are duplicated here
// because each Babel script is scope-isolated (only window.* crosses files).
const KB_MIME_BY_EXT = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword', xls: 'application/vnd.ms-excel', ppt: 'application/vnd.ms-powerpoint',
  txt: 'text/plain', csv: 'text/csv',
};
const kbIconByExt = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['png','jpg','jpeg','webp','gif'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['ppt','pptx'].includes(ext)) return '📽️';
  return '📎';
};
const kbFmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const KBAttachmentsBlock = ({ articleId }) => {
  const [items, setItems] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef();

  const reload = React.useCallback(() => {
    window.IT_AUTH.api('GET', `/kb/${articleId}/attachments`, undefined, 'kbAttachments')
      .then(list => setItems(Array.isArray(list) ? list : []))
      .catch(e => { console.warn('[kb-attachments]', e.message); setItems([]); });
  }, [articleId]);

  React.useEffect(() => { reload(); }, [reload]);

  const uploadOne = async (file) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const declared = file.type || KB_MIME_BY_EXT[ext] || '';
    if (!declared) { setError(`Unsupported file: ${file.name}`); return; }
    if (file.size > 25 * 1024 * 1024) { setError(`${file.name} is too large (max 25 MB)`); return; }
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    const b64 = btoa(bin);
    try {
      await window.IT_AUTH.api('POST', `/kb/${articleId}/attachments`, {
        mimeType: declared, data: b64, filename: file.name,
      }, 'kbAttachmentUpload');
    } catch (e) { setError(e.message); }
  };

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setError(null);
    setUploading(true);
    try {
      for (const f of fileList) await uploadOne(f);
      reload();
    } catch (e) { setError(e.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove ${att.fileName}?`)) return;
    try {
      await window.IT_AUTH.api('DELETE', `/kb/${articleId}/attachments/${att.id}`, undefined, 'kbAttachmentDelete');
      reload();
    } catch (e) { setError(e.message); }
  };

  // Authenticated download (bearer token) → object URL → save as original name.
  const download = async (att) => {
    try {
      const token = await window.IT_AUTH.getApiToken();
      const apiBase = 'https://it-ticketing-api-dcgwgjerducxg8ck.uksouth-01.azurewebsites.net/api';
      const res = await fetch(`${apiBase}/kb/${articleId}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div style={{marginTop: 28}}>
      <div style={{fontSize:12, fontWeight:600, color:"var(--text-subtle)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8, display:"flex", alignItems:"center", gap:8}}>
        <Icon name="paperclip" size={12}/>
        Attachments
        {items != null && <span style={{fontWeight:400}}>· {items.length}</span>}
      </div>

      {items === null ? (
        <div style={{fontSize:12, color:"var(--text-subtle)", padding:"6px 0"}}>Loading…</div>
      ) : (
        <>
          {items.length > 0 && (
            <div style={{display:"flex", flexDirection:"column", gap:6, marginBottom:10}}>
              {items.map(att => (
                <div key={att.id} style={{
                  display:"flex", alignItems:"center", gap:10,
                  padding:"8px 10px", border:"1px solid var(--line)", borderRadius:6,
                  background:"var(--bg-elev)",
                }}>
                  <span style={{fontSize:18, lineHeight:1, flexShrink:0}}>{kbIconByExt(att.fileName)}</span>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:500, color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {att.fileName}
                    </div>
                    <div style={{fontSize:11, color:"var(--text-subtle)", marginTop:1}}>
                      {kbFmtBytes(att.size)}
                      {att.uploadedBy && ` · by ${att.uploadedBy.split('@')[0]}`}
                    </div>
                  </div>
                  <button
                    onClick={() => download(att)}
                    style={{background:"none", border:"1px solid var(--line)", borderRadius:5, padding:"4px 10px", fontSize:12, cursor:"pointer", color:"var(--text)"}}
                  >
                    Download
                  </button>
                  <button
                    onClick={() => remove(att)}
                    title="Delete attachment"
                    style={{background:"none", border:"none", padding:"4px 6px", cursor:"pointer", color:"var(--text-subtle)", fontSize:15, lineHeight:1}}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding:"12px", textAlign:"center",
              border:`1.5px dashed ${dragOver ? 'var(--accent)' : 'var(--line)'}`,
              background: dragOver ? 'var(--bg-sub)' : 'transparent',
              borderRadius:6, cursor: uploading ? 'wait' : 'pointer',
              fontSize:12, color:"var(--text-subtle)",
              transition:'background 120ms ease, border-color 120ms ease',
            }}
          >
            {uploading
              ? 'Uploading…'
              : <>Drag &amp; drop files here, or click to browse. <span>Max 25 MB each. PDF, images, Office docs, text/CSV.</span></>
            }
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{display:"none"}}
              onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          {error && <div style={{marginTop:8, fontSize:12, color:"#DC2626"}}>{error}</div>}
        </>
      )}
    </div>
  );
};

// Fix the article body renderer - let me rewrite it as a cleaner walker
const renderBlock = (block, i) => {
  const [type, content, ...rest] = block;
  if (type === "h2") return <h2 key={i}>{content}</h2>;
  if (type === "p") {
    if (Array.isArray(content) && content.length > 0 && typeof content[0] !== "string") {
      return <p key={i}>{content.map((c, j) => Array.isArray(c) ? <code key={j}>{c[1]}</code> : linkify(c))}</p>;
    }
    const inlines = [content, ...rest];
    return <p key={i}>{inlines.map((c, j) => Array.isArray(c) ? <code key={j}>{c[1]}</code> : <React.Fragment key={j}>{linkify(c)}</React.Fragment>)}</p>;
  }
  if (type === "ul") return <ul key={i}>{content.map((li, j) => <li key={j}>{linkify(li)}</li>)}</ul>;
  if (type === "ol") return <ol key={i}>{content.map((li, j) => <li key={j}>{linkify(li)}</li>)}</ol>;
  return null;
};

// Override the body section of KBArticleView to use renderBlock
export { KBView };
export { renderBlock };
export { KBAttachmentsBlock };